import { __read, __spreadArray } from "tslib";
import { QueryExpType, } from '@tencent/memoire-core';
import { UnimplementedError } from '@tencent/memoire-error-utils';
import { StorageEvent } from '@tencent/memoire-structured-storage';
import { SLOW_QUERY_LIMIT, BEGIN, Tags } from './constants';
import { StorageTag, MethodTag, ArgumentTags, EventTag, createCostTracer, ModuleTag } from './tag';
/**
 * LoggerPlugin is used for logging the internal operations of the storage.
 * You can use your own `LogProvider`. The default log provider is `console`.
 */
var LoggerPlugin = /** @class */ (function () {
    function LoggerPlugin(_a) {
        var _this = this;
        var _b = _a === void 0 ? {
            logger: console,
            verbose: false,
            disabled: false,
            prefix: '',
        } : _a, _c = _b.logger, logger = _c === void 0 ? console : _c, _d = _b.disabled, disabled = _d === void 0 ? false : _d, _e = _b.verbose, verbose = _e === void 0 ? false : _e, _f = _b.prefix, prefix = _f === void 0 ? '' : _f;
        this.name = 'LoggerPlugin';
        this.formatQueryConditionsExp = function (exp) {
            if (exp.type === QueryExpType.AND || exp.type === QueryExpType.OR) {
                if (!exp.exps.length)
                    return undefined;
                return exp.exps
                    .map(_this.formatQueryConditionsExp)
                    .filter(Boolean)
                    .map(function (item) { return (exp.type === QueryExpType.OR && exp.exps.length > 1 ? "(" + item + ")" : item); })
                    .join(exp.type === QueryExpType.AND ? ' AND ' : ' OR ');
            }
            if (exp.type === QueryExpType.FILTER) {
                return _this.formatPredicate(exp.path, exp.predicate);
            }
            return exp.type;
        };
        this.formatPredicate = function (path, predicate) {
            return Object.keys(predicate)
                .filter(function (op) { return predicate[op] !== undefined; })
                .map(function (op) { return path + " " + op + " " + JSON.stringify(predicate[op]); })
                .join(' AND ');
        };
        this.logMethodBegin = function (ctx, module, methodName, args) {
            var _a;
            ctx.timings.set(BEGIN, new Date());
            (_a = _this.logger).info.apply(_a, __spreadArray([_this.prefix, Tags.pending, MethodTag(module, methodName)], __read(ArgumentTags(args))));
        };
        this.logMethodEnd = function (ctx, module, methodName, args, result) {
            var _a, _b;
            var _c, _d;
            var begin = ctx.timings.get(BEGIN) || new Date();
            var methodTag = MethodTag(module, methodName);
            var cost = Date.now() - begin.getTime();
            var costTag = "cost=" + cost + "ms";
            var isSlowQuery = cost >= SLOW_QUERY_LIMIT;
            if (result.error) {
                (_a = _this.logger).error.apply(_a, __spreadArray([_this.prefix,
                    Tags.failure,
                    MethodTag(module, methodName),
                    costTag,
                    (_c = result.error) === null || _c === void 0 ? void 0 : _c.message,
                    (_d = result.error) === null || _d === void 0 ? void 0 : _d.stack], __read(ArgumentTags(args))));
                return;
            }
            (_b = _this.logger)[isSlowQuery ? 'warn' : 'info'].apply(_b, __spreadArray(__spreadArray(__spreadArray(__spreadArray([_this.prefix,
                Tags.success], __read((isSlowQuery ? [Tags.slow] : []))), [methodTag,
                costTag]), __read(ArgumentTags(args))), ['result:',
                result.data]));
        };
        this.logger = logger;
        this.disabled = disabled;
        this.verbose = verbose;
        this.prefix = prefix;
    }
    LoggerPlugin.prototype.register = function (storage) {
        if (this.disabled)
            return;
        this.prefix = this.prefix || StorageTag(storage.name);
        this.hookPrimaryKeyQueries(storage);
        this.hookConditionQueries(storage);
        this.hookDataManipulationQueries(storage);
        this.hookEvents(storage);
        this.hookMigration(storage);
        if (this.verbose) {
            this.hookEngine(storage);
        }
    };
    LoggerPlugin.prototype.dispose = function (storage) {
        throw new UnimplementedError({ storage: storage });
    };
    LoggerPlugin.prototype.hookMigration = function (storage) {
        var _this = this;
        var hook = function (aspect, methodName, module) {
            if (module === void 0) { module = 'migration'; }
            aspect
                .before(function (ctx) {
                _this.logMethodBegin(ctx, module, methodName, ctx.args);
            })
                .after(function (ctx) {
                _this.logMethodEnd(ctx, module, methodName, ctx.args, { data: ctx.return, error: ctx.error });
            });
        };
        hook(storage.aspects.migration.buildMigration, 'buildMigration');
        hook(storage.aspects.migration.executeDataMigration, 'executeDataMigration');
        hook(storage.aspects.migration.executeSchemaMigration, 'executeSchemaMigration');
        hook(storage.aspects.migration.updateMigrationChangeInfo, 'updateMigrationChangeInfo');
        hook(storage.aspects.migration.prepareAutoUpgradeHandler, 'prepareAutoUpgradeHandler');
        hook(storage.aspects.migration.log, 'innerLog');
        hook(storage.aspects.migration.actionExecutor.executeSchemaAction, 'executeSchemaAction');
        hook(storage.aspects.migration.actionExecutor.executeDataAction, 'executeDataAction');
    };
    // hook engine all core method
    LoggerPlugin.prototype.hookEngine = function (storage) {
        var _this = this;
        var hook = function (aspect, model, methodName) {
            aspect
                .before(function (ctx) {
                _this.logMethodBegin(ctx, model, methodName, ctx.args);
            })
                .after(function (ctx) {
                _this.logMethodEnd(ctx, model, methodName, ctx.args, { data: ctx.return, error: ctx.error });
            });
        };
        var engineAspects = storage.aspects.engine;
        hook(engineAspects.openDB, 'engine', 'openDB');
        hook(engineAspects.deleteDB, 'engine', 'deleteDB');
        this.hookEngineDatabase(engineAspects.database, hook);
        this.hookEngineTable(engineAspects.table, hook);
        this.hookEngineTransaction(engineAspects.transaction, hook);
        this.hookEngineIndex(engineAspects.index, hook);
        this.hookEngineCursor(engineAspects.cursor, hook);
    };
    // hook database method
    LoggerPlugin.prototype.hookEngineDatabase = function (databaseAspect, hook) {
        var databaseModelTag = ModuleTag('engine', 'database');
        hook(databaseAspect.close, databaseModelTag, 'close');
        hook(databaseAspect.table, databaseModelTag, 'table');
        hook(databaseAspect.transaction, databaseModelTag, 'transaction');
        hook(databaseAspect.createTable, databaseModelTag, 'createTable');
        hook(databaseAspect.deleteTable, databaseModelTag, 'deleteTable');
    };
    // hook all transaction methods and log begin and end;
    LoggerPlugin.prototype.hookEngineTransaction = function (transactionAspects, hook) {
        var transactionTag = ModuleTag('engine', 'transaction');
        hook(transactionAspects.commit, transactionTag, 'commit');
        hook(transactionAspects.rollback, transactionTag, 'rollback');
        hook(transactionAspects.table, transactionTag, 'table');
    };
    // hook all index methods and log begin and end;
    LoggerPlugin.prototype.hookEngineIndex = function (indexAspects, hook) {
        var indexTag = ModuleTag('engine', 'index');
        hook(indexAspects.get, indexTag, 'get');
        hook(indexAspects.getAll, indexTag, 'getAll');
        hook(indexAspects.getAllKeys, indexTag, 'getAllKeys');
        hook(indexAspects.openCursor, indexTag, 'openCursor');
    };
    // hook all cursor methods and log begin and end;
    LoggerPlugin.prototype.hookEngineCursor = function (cursorAspects, hook) {
        var cursorTag = ModuleTag('engine', 'cursor');
        hook(cursorAspects.delete, cursorTag, 'delete');
        hook(cursorAspects.update, cursorTag, 'update');
        hook(cursorAspects.next, cursorTag, 'next');
        hook(cursorAspects.value, cursorTag, 'value');
    };
    // hook all table methods and log begin and end;
    LoggerPlugin.prototype.hookEngineTable = function (tableAspects, hook) {
        var tableModelTag = ModuleTag('engine', 'table');
        hook(tableAspects.clear, tableModelTag, 'clear');
        hook(tableAspects.get, tableModelTag, 'get');
        hook(tableAspects.delete, tableModelTag, 'delete');
        hook(tableAspects.getAll, tableModelTag, 'getAll');
        hook(tableAspects.getAllKeys, tableModelTag, 'getAllKeys');
        hook(tableAspects.index, tableModelTag, 'index');
        hook(tableAspects.insert, tableModelTag, 'insert');
        hook(tableAspects.upsert, tableModelTag, 'upsert');
        hook(tableAspects.createIndex, tableModelTag, 'createIndex');
        hook(tableAspects.deleteIndex, tableModelTag, 'deleteIndex');
        hook(tableAspects.openCursor, tableModelTag, 'openCursor');
        hook(tableAspects.query, tableModelTag, 'query');
        hook(tableAspects.bulkDelete, tableModelTag, 'bulkDelete');
        hook(tableAspects.bulkUpsert, tableModelTag, 'bulkUpsert');
    };
    LoggerPlugin.prototype.hookPrimaryKeyQueries = function (storage) {
        var _this = this;
        var hook = function (aspect, action) {
            aspect
                .before(function (ctx) {
                _this.logMethodBegin(ctx, ctx.this.name, action, { key: ctx.args[0] });
            })
                .after(function (ctx) {
                _this.logMethodEnd(ctx, ctx.this.name, action, { key: ctx.args[0] }, { data: ctx.return, error: ctx.error });
            });
        };
        hook(storage.aspects.collection.get, 'get');
        hook(storage.aspects.collection.has, 'has');
        hook(storage.aspects.collection.delete, 'delete');
    };
    LoggerPlugin.prototype.hookConditionQueries = function (storage) {
        var _this = this;
        var hook = function (aspect, action) {
            aspect
                .before(function (ctx) {
                _this.logMethodBegin(ctx, ctx.this.name, action, _this.formatQueryConditions(ctx.this.conditions()));
            })
                .after(function (ctx) {
                _this.logMethodEnd(ctx, ctx.this.name, action, _this.formatQueryConditions(ctx.this.conditions()), {
                    data: ctx.return,
                    error: ctx.error,
                });
            });
        };
        hook(storage.aspects.collection.count, 'count');
        hook(storage.aspects.collection.entries, 'entries');
        hook(storage.aspects.collection.keys, 'keys');
        hook(storage.aspects.collection.values, 'values');
        hook(storage.aspects.collection.first, 'first');
        hook(storage.aspects.collection.last, 'last');
        hook(storage.aspects.collection.clear, 'clear');
        hook(storage.aspects.collection.update, 'update');
    };
    LoggerPlugin.prototype.hookDataManipulationQueries = function (storage) {
        var _this = this;
        var hook = function (aspect, action) {
            aspect
                .before(function (ctx) {
                _this.logMethodBegin(ctx, ctx.this.name, action, ctx.args);
            })
                .after(function (ctx) {
                _this.logMethodEnd(ctx, ctx.this.name, action, ctx.args, { data: ctx.return, error: ctx.error });
            });
        };
        hook(storage.aspects.collection.insert, 'insert');
        hook(storage.aspects.collection.upsert, 'upsert');
        hook(storage.aspects.collection.bulkInsert, 'bulkInsert');
        hook(storage.aspects.collection.bulkUpsert, 'bulkUpsert');
    };
    LoggerPlugin.prototype.hookEvents = function (storage) {
        var _this = this;
        var CostTag = createCostTracer();
        storage.events.onBootstrapStart(function () {
            _this.logger.info(_this.prefix, EventTag(StorageEvent.BootstrapStart), CostTag());
        });
        storage.events.onBootstrapEnd(function () {
            _this.logger.info(_this.prefix, EventTag(StorageEvent.BootstrapEnd), CostTag());
        });
        storage.events.onSchemaMigrationStart(function () {
            _this.logger.info(_this.prefix, EventTag(StorageEvent.SchemaMigrationStart), CostTag());
        });
        storage.events.onSchemaMigrationEnd(function () {
            _this.logger.info(_this.prefix, EventTag(StorageEvent.SchemaMigrationEnd), CostTag());
        });
        storage.events.onRegisterModel(function (_a) {
            var _b;
            var schema = _a.schema;
            return (_b = _this.logger).info.apply(_b, __spreadArray(__spreadArray([_this.prefix,
                EventTag(StorageEvent.RegisterModel)], __read(ArgumentTags({ schema: JSON.stringify(schema) }))), [CostTag()]));
        });
        storage.events.onRegisterPlugin(function (_a) {
            var _b;
            var name = _a.name;
            return (_b = _this.logger).info.apply(_b, __spreadArray(__spreadArray([_this.prefix, EventTag(StorageEvent.RegisterPlugin)], __read(ArgumentTags({ name: name }))), [CostTag()]));
        });
        storage.events.onReady(function (_a) {
            var _b;
            var version = _a.version, tableNames = _a.tableNames;
            return (_b = _this.logger).info.apply(_b, __spreadArray(__spreadArray([_this.prefix,
                EventTag(StorageEvent.Ready)], __read(ArgumentTags({ version: version, collections: Array.from(tableNames) }))), [CostTag()]));
        });
    };
    LoggerPlugin.prototype.formatQueryConditions = function (conditions) {
        var offset = conditions.offset, limit = conditions.limit, order = conditions.order, exp = conditions.exp;
        return {
            OFFSET: offset || undefined,
            LIMIT: limit,
            ORDER_BY: order && "(" + order.path + " " + order.type + ")",
            WHERE: "(" + this.formatQueryConditionsExp(exp) + ")",
        };
    };
    return LoggerPlugin;
}());
export default LoggerPlugin;
//# sourceMappingURL=logger-plugin.js.map